import React, { useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Trophy, Flag, Handshake, PlayCircle, ArrowRight, ArrowsClockwise,
  X, CircleNotch, House, ShareNetwork,
} from "@phosphor-icons/react";
import { shareGame } from "@/lib/share";
import Avatar2D from "@/components/Avatar2D";
import SendFeedback from "@/components/SendFeedback";
import GameStats from "@/components/GameStats";

// Flag colours on the board: yours is pink, the opponent's is blue.
const MY_FLAG = "#e0457a";
const OPP_FLAG = "#1d74c9";

/**
 * Pops up immediately after a match ends. Shows the result (win/loss/draw),
 * elo delta, and three actions:
 *   * Game review — opens the step-through ReviewModal
 *   * Share       — sends a link to the game's step-through (lib/share.js)
 *   * Rematch     — challenge the same opponent again
 *   * New Game    — back to lobby with auto-search
 *   * Main menu   — back to lobby without searching
 *   * Send feedback (small link at the bottom) — the same form as the menu
 *
 * `rematchState` drives the rematch UX:
 *   - "idle"     : show 3 standard buttons
 *   - "pending"  : we sent a rematch request — show "Waiting for opponent…" + Cancel
 *   - "incoming" : opponent sent us a rematch request — show Accept / Decline
 *   - "declined" : opponent declined our request — show a brief message + reset
 */
export default function GameOverModal({
  open, onOpenChange, result, eloDelta, oppUsername, winReason,
  rematchState = "idle",
  onReview, onRematch, onRematchAccept, onRematchDecline, onRematchCancel,
  onNewGame, onMainMenu, isBot = false, meCharacter = null, oppCharacter = null,
  matchId = null, meUsername = "", race = false, stats = null, colours = null,
}) {
  // "draw" is an agreed draw (Offer draw in the game header). Anything else
  // that isn't a win shows as a defeat.
  const isWin = result === "win";
  const isDraw = result === "draw";
  const Icon = isWin ? Trophy : isDraw ? Handshake : Flag;
  const headline = isWin ? "Victory" : isDraw ? "Draw" : "Defeat";
  const tone = isWin ? "text-[hsl(var(--chart-4))]" : isDraw ? "text-foreground" : "text-destructive";
  // The winner's raider, holding the loser's flag. Nobody holds one in a draw.
  const raider = isDraw ? null : isWin ? meCharacter : oppCharacter;

  // Bot matches: hide rematch negotiation UI, keep the simple Rematch button
  const showRematchNegotiation = !isBot;
  const [feedbackOpen, setFeedbackOpen] = useState(false);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="game-over-modal">
        <DialogHeader className="text-center items-center">
          {raider ? (
            <div
              className={`mx-auto w-36 h-36 rounded-full flex items-center justify-center mb-1
                ${isWin ? "bg-[hsl(var(--chart-4))]/15" : "bg-destructive/15"}`}
              data-testid="game-over-raider"
            >
              <Avatar2D
                character={raider}
                pose="cheer"
                flag={isWin ? OPP_FLAG : MY_FLAG}
                className="w-[128px] aspect-[240/256]"
              />
            </div>
          ) : (
            <div
              className={`mx-auto w-16 h-16 rounded-full flex items-center justify-center mb-2
                ${isWin ? "bg-[hsl(var(--chart-4))]/15" : isDraw ? "bg-muted" : "bg-destructive/15"}`}
            >
              <Icon size={36} weight={isWin || isDraw ? "fill" : "regular"} className={tone} />
            </div>
          )}
          <DialogTitle className={`font-display text-4xl font-black tracking-tight ${tone}`}>
            {headline}
          </DialogTitle>
          <DialogDescription className="text-sm">
            {isDraw ? <>Drawn with {oppUsername}.{winReason && <> · <span className="opacity-80">{winReason}</span></>}</>
              : isWin ? <>You beat {oppUsername}.{winReason && <> · <span className="opacity-80">{winReason}</span></>}</>
              : winReason && <span className="opacity-80">{winReason}</span>}
          </DialogDescription>
        </DialogHeader>

        {/* Elo delta pill */}
        <div className="flex items-center justify-center mt-1">
          <div className={`px-4 py-1.5 rounded-full text-sm font-display font-bold border
            ${!isBot && typeof eloDelta === "number" && eloDelta > 0
              ? "bg-[hsl(var(--chart-4))]/10 border-[hsl(var(--chart-4))]/30 text-[hsl(var(--chart-4))]"
              : !isBot && typeof eloDelta === "number" && eloDelta < 0
              ? "bg-destructive/10 border-destructive/30 text-destructive"
              : "bg-muted text-muted-foreground border-border"}`}
          >
            {isBot
              ? "Bot game · Elo unchanged"
              : race
              ? "Raider Races · Elo unchanged"
              : <>Elo {typeof eloDelta === "number" ? (eloDelta >= 0 ? `+${eloDelta}` : eloDelta) : "—"}</>}
          </div>
        </div>

        {stats && <GameStats stats={stats} oppUsername={oppUsername} colours={colours} />}

        {/* Buttons */}
        <div className={`grid grid-cols-1 gap-2 ${stats ? "mt-3" : "mt-5"}`}>
          {/* Raider Races have no step-through review (the board isn't mirrored). */}
          {!race && <div className={`grid gap-2 ${matchId ? "grid-cols-2" : "grid-cols-1"}`}>
            <Button
              onClick={onReview}
              variant="outline"
              className="rounded-full h-11 gap-2"
              data-testid="game-over-review-btn"
            >
              <PlayCircle size={18} weight="fill" /> Game review
            </Button>
            {matchId && (
              <Button
                onClick={() => shareGame({
                  matchId,
                  title: `${meUsername || "Jangle"} vs ${oppUsername} · Jangle`,
                  text: "Step through our Jangle game:",
                })}
                variant="outline"
                className="rounded-full h-11 gap-2"
                data-testid="game-over-share-btn"
              >
                <ShareNetwork size={18} weight="bold" /> Share
              </Button>
            )}
          </div>}

          {/* Rematch UX — varies by state */}
          {showRematchNegotiation && rematchState === "incoming" && (
            <div className="space-y-2" data-testid="rematch-incoming">
              <p className="text-center text-sm text-muted-foreground">
                <span className="font-display font-bold text-foreground">{oppUsername}</span> wants a rematch
              </p>
              <div className="grid grid-cols-2 gap-2">
                <Button
                  onClick={onRematchDecline}
                  variant="outline"
                  className="rounded-full h-11 gap-2 border-destructive/40 text-destructive hover:bg-destructive/10"
                  data-testid="rematch-decline-btn"
                >
                  <X size={16} weight="bold" /> Decline
                </Button>
                <Button
                  onClick={onRematchAccept}
                  className="rounded-full h-11 gap-2 bg-brand hover:bg-brand-hover text-brand-foreground"
                  data-testid="rematch-accept-btn"
                >
                  <ArrowsClockwise size={16} weight="bold" /> Accept
                </Button>
              </div>
            </div>
          )}

          {showRematchNegotiation && rematchState === "pending" && (
            <div
              className="rounded-full h-11 px-4 flex items-center justify-center gap-2 border border-input bg-background text-muted-foreground text-sm font-medium"
              data-testid="rematch-pending-btn"
            >
              <CircleNotch size={16} weight="bold" className="animate-spin" />
              <span>Waiting for {oppUsername}…</span>
              <button
                type="button"
                aria-label="Cancel rematch"
                onClick={onRematchCancel}
                className="ml-auto inline-flex items-center justify-center w-6 h-6 rounded-full hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors"
                data-testid="rematch-cancel-btn"
              >
                <X size={14} weight="bold" />
              </button>
            </div>
          )}

          {showRematchNegotiation && rematchState === "declined" && (
            <div
              className="rounded-full h-11 px-4 flex items-center justify-center border border-destructive/30 bg-destructive/5 text-destructive text-sm font-medium"
              data-testid="rematch-declined-banner"
            >
              {oppUsername} declined the rematch
            </div>
          )}

          {(rematchState === "idle" || !showRematchNegotiation) && (
            <Button
              onClick={onRematch}
              className="rounded-full h-11 gap-2 bg-brand hover:bg-brand-hover text-brand-foreground"
              data-testid="game-over-rematch-btn"
            >
              <ArrowsClockwise size={18} weight="bold" /> Rematch
            </Button>
          )}

          <Button
            onClick={onNewGame}
            variant="outline"
            className="rounded-full h-11 gap-2 border-foreground/30 hover:border-foreground/60"
            data-testid="game-over-new-game-btn"
          >
            New Game <ArrowRight size={18} weight="bold" />
          </Button>

          {onMainMenu && (
            <Button
              onClick={onMainMenu}
              variant="ghost"
              className="rounded-full h-11 gap-2"
              data-testid="game-over-main-menu-btn"
            >
              <House size={18} weight="bold" /> Main menu
            </Button>
          )}
        </div>

        <button
          type="button"
          onClick={() => setFeedbackOpen(true)}
          className="mx-auto mt-1 text-sm text-muted-foreground hover:text-foreground underline-offset-4 hover:underline transition-colors"
          data-testid="game-over-feedback-btn"
        >
          Send feedback
        </button>
        <SendFeedback open={feedbackOpen} onOpenChange={setFeedbackOpen} />
      </DialogContent>
    </Dialog>
  );
}
